import { Fragment, useMemo, useState } from "react";
import {
    LabelValue,
    ListSection,
    billingRateRowToCells,
    buildBillingRateRows,
    buildBillingRateTotalsBreakdown,
    computeBillingRateTotals,
    currencyFmt,
    getBillingRateHeaders,
    getCoverageTierHeaders,
} from "./BillingRateBuilderShared";
import BillingRateBuilderTotalsBreakdownModal from "./BillingRateBuilderTotalsBreakdownModal";
import { validateRatePayload } from "./billingRateBuilderSchemas";

const hasValue = (v) => v != null && v !== "";

const getActivePartialIntervalField = (cat) => {
    if (hasValue(cat?.minimumEnrolledDaysForFullPeriodBilling)) {
        return {
            label: "Minimum Enrolled Days For Full Period Billing",
            value: cat.minimumEnrolledDaysForFullPeriodBilling,
        };
    }
    if (hasValue(cat?.prorationByNumberOfDaysPerMonth)) {
        return {
            label: "Proration By Number Of Days Per Month",
            value: cat.prorationByNumberOfDaysPerMonth,
        };
    }
    if (hasValue(cat?.washDay)) {
        return {
            label: "Wash Day",
            value: cat.washDay,
        };
    }
    return null;
};

const getDefMeta = (def) => ({
    name: def?.componentReference?.component ?? "",
    lockDate: def?.componentReference?.lockDate ?? "",
});

// Collects all error messages for a billing category's rate definitions.
const getDefsErrors = (errors, catIdx) => {
    const listErr = errors[`billingCategoryRates.${catIdx}.billingRateDefinitions`];
    const itemMessages = Object.entries(errors)
        .filter(([k]) => k.startsWith(`billingCategoryRates.${catIdx}.billingRateDefinitions.`))
        .map(([, v]) => v);
    return [...new Set(listErr ? [listErr, ...itemMessages] : itemMessages)];
};

const RateDetailModalContent = ({
    payload,
    componentsByName,
    billingCategoryRows,
    fees,
    uploadContext,
    plans,
}) => {
    const [isBreakdownOpen, setIsBreakdownOpen] = useState(false);
    const billingRate = payload?.billingRatePayload?.billingRate;
    const benefitPlanId = payload?.benefitPlan ?? payload?.plan?.name;
    const tierHeaders = getCoverageTierHeaders(uploadContext, benefitPlanId);
    const billingCategoryRates = billingRate?.billingCategoryRates;
    const { totals, pmpmRates } = computeBillingRateTotals(
        billingCategoryRates,
        componentsByName,
        fees,
        tierHeaders,
    );
    const totalsBreakdown = useMemo(
        () => buildBillingRateTotalsBreakdown(
            billingCategoryRates,
            componentsByName,
            fees,
            tierHeaders,
        ),
        [billingCategoryRates, componentsByName, fees, tierHeaders],
    );
    const expectedTotals = useMemo(
        () => (plans ?? []).find(
            (plan) => plan?.planId === benefitPlanId || plan?.name === benefitPlanId,
        )?.billingRateTotals ?? null,
        [plans, benefitPlanId],
    );

    const errors = validateRatePayload(payload).fieldErrors;

    return (
        <div className="billing-rate-builder__modal-content-body">
            <div className="billing-rate-builder__top-row">
                <div className="billing-rate-builder__label-grid billing-rate-builder__label-grid--inline">
                    <LabelValue
                        label="Account"
                        value={payload?.account ?? billingRate?.account}
                        error={errors["account"]}
                    />
                    <LabelValue
                        label="Benefit Plan"
                        value={payload?.benefitPlan ?? payload?.plan?.name}
                        error={errors["benefitPlan"]}
                    />
                </div>

                <div className="billing-rate-builder__totals-section">
                    <div className="billing-rate-builder__totals-header">
                        <p className="label billing-rate-builder__label">Billing Rate Totals</p>
                        <button
                            type="button"
                            className="billing-rate-builder__text-link"
                            onClick={() => setIsBreakdownOpen(true)}
                        >
                            View calculation details
                        </button>
                    </div>
                    <div className="billing-rate-builder__totals-box">
                        {tierHeaders.map((tier, i) => (
                            <Fragment key={tier}>
                                <span className="billing-rate-builder__totals-label">{tier}</span>
                                <span className="billing-rate-builder__totals-value">
                                    {currencyFmt.format(totals[i])}
                                </span>
                            </Fragment>
                        ))}
                    </div>
                </div>

                {pmpmRates.length > 0 && (
                    <div className="billing-rate-builder__totals-section">
                        <p className="label billing-rate-builder__label">PMPM Totals</p>
                        <div className="billing-rate-builder__totals-box">
                            {pmpmRates.map((entry, i) => (
                                <Fragment key={i}>
                                    <span className="billing-rate-builder__totals-label">{entry.label}</span>
                                    <span className="billing-rate-builder__totals-value">
                                        {currencyFmt.format(entry.amount)}
                                    </span>
                                </Fragment>
                            ))}
                        </div>
                    </div>
                )}

            </div>

            <p className="title billing-rate-builder__section-title">Billing Category Rates</p>
            {errors["billingCategoryRates"] && (
                <p style={{ color: "var(--red)", paddingTop: ".4rem", fontSize: ".85rem" }}>
                    {errors["billingCategoryRates"]}
                </p>
            )}

            {(billingRate?.billingCategoryRates ?? []).map((cat, idx) => {
                const codeEntry = cat?.billingCategory?.codeEntry ?? "";
                const matchedCategory = (billingCategoryRows ?? []).find(
                    (r) => r?.cdEntryTxt === codeEntry,
                );
                const shortNm =
                    matchedCategory?.shortNm ?? payload?.billingCategoryShortNames?.[codeEntry] ?? "";
                const cdEntryTxt = matchedCategory?.cdEntryTxt ?? codeEntry;
                const categoryFullName = shortNm
                    ? `${shortNm} (${cdEntryTxt})`
                    : codeEntry;
                const activePartialField = getActivePartialIntervalField(cat);
                const { rowsByDef, anyTiered } = buildBillingRateRows(
                    cat?.billingRateDefinitions,
                    componentsByName,
                    getDefMeta,
                );
                const headers = getBillingRateHeaders(anyTiered);
                const rows = rowsByDef
                    .flat()
                    .map((r) => billingRateRowToCells(r, anyTiered));

                const catBillingCategoryError = errors[`billingCategoryRates.${idx}.billingCategory`];
                const catPartialIntervalError = errors[`billingCategoryRates.${idx}.partialIntervalCalculationMethod`];
                const defsErrors = getDefsErrors(errors, idx);

                return (
                    <div key={idx} className="billing-rate-builder__card">
                        <p className="sub-title billing-rate-builder__branch-title">
                            {shortNm ? `Billing Rate Component - (${shortNm})` : "Billing Rate Component"}
                        </p>

                        <LabelValue
                            label="Billing Category"
                            value={categoryFullName}
                            error={catBillingCategoryError}
                        />

                        <div className="billing-rate-builder__parameters-section">
                            <p className="sub-title billing-rate-builder__subtitle-emphasis">
                                Billing Parameters
                            </p>
                            <div className="billing-rate-builder__label-grid">
                                <LabelValue
                                    label="Partial Interval Calculation Method"
                                    value={cat?.partialIntervalCalculationMethod}
                                    error={catPartialIntervalError}
                                />
                                <LabelValue
                                    label={activePartialField?.label ?? "Wash Day"}
                                    value={activePartialField?.value ?? ""}
                                />
                            </div>
                        </div>

                        <ListSection
                            label="Billing Party Percentages"
                            headers={["Billing Party", "Bill Percentage"]}
                            rows={(cat?.billingPartyPercentages ?? []).map((bpp) => [
                                bpp?.billingParty,
                                bpp?.billPercentage,
                            ])}
                        />

                        {defsErrors.map((msg, i) => (
                            <p key={i} style={{ color: "var(--red)", paddingTop: ".4rem", fontSize: ".85rem" }}>
                                {msg}
                            </p>
                        ))}

                        <ListSection
                            label="Billing Rates"
                            headers={headers}
                            rows={rows}
                        />
                    </div>
                );
            })}
            <BillingRateBuilderTotalsBreakdownModal
                isOpen={isBreakdownOpen}
                onClose={() => setIsBreakdownOpen(false)}
                title={`${payload?.benefitPlan ?? payload?.plan?.name ?? "Billing rate"} — calculation details`}
                breakdown={totalsBreakdown}
                expectedTotals={expectedTotals}
            />
        </div>
    );
};

export default RateDetailModalContent;
